import * as Yup from "yup";
import { enums } from "../constants/enum";

const objectIdPattern = /^[0-9a-fA-F]{24}$/;

// EYE MEASUREMENT SCHEMA FOR DISTANCE AND NEAR
// Helper to validate Number or 'PLANO'
const numberOrPlano = (min, max, label) =>
  Yup.mixed()
    .nullable()
    .test(
      "is-valid-format",
      `${label} must be a number or 'PLANO'`,
      (value) => {
        if (value === null || value === undefined || value === "") return true;
        if (value === "PLANO") return true;
        return !isNaN(parseFloat(value)) && isFinite(value);
      },
    )
    .test("min", `${label} must be at least ${min.toFixed(1)}`, (value) => {
      if (value === "PLANO" || value == null || value === "") return true;
      return parseFloat(value) >= min;
    })
    .test("max", `${label} must not exceed ${max.toFixed(1)}`, (value) => {
      if (value === "PLANO" || value == null || value === "") return true;
      return parseFloat(value) <= max;
    })
    .test(
      "step",
      `${label} must be in steps of 0.25 (e.g., -1.25, -1.50)`,
      (value) => {
        if (value === "PLANO" || value == null || value === "") return true;
        // Use Math.round to handle float precision
        return Math.round(parseFloat(value) * 100) % 25 === 0;
      },
    );

// EYE MEASUREMENT SCHEMA FOR DISTANCE AND NEAR
const eyeMeasurementSchema = Yup.object().shape({
  sph: numberOrPlano(-28.0, 28.0, "SPH"),
  cyl: numberOrPlano(-6.0, 6.0, "CYL"),
  axis: Yup.number()
    .min(-180, "AXIS must be at least -180")
    .max(180, "AXIS must not exceed 180")
    .nullable()
    .typeError("AXIS must be a number"),
});

// FRAME OBJECT SCHEMA
const frameSchema = Yup.object().shape({
  selectedFrame: Yup.string()
    .trim()
    .matches(objectIdPattern, "Frame selection is required")
    .required("Frame selection is required"),
  pd: Yup.number()
    .min(0, "PD must be a positive number")
    .required("PD (Pupillary Distance) is required")
    .typeError("PD must be a number"),
});

// LENSE OBJECT SCHEMA
const lenseSchema = Yup.object().shape({
  right: Yup.object()
    .shape({
      selectedLense: Yup.string()
        .trim()
        .matches(objectIdPattern, "Right lense selection is required")
        .required("Right lense selection is required"),
      diameter: Yup.number()
        .min(0, "Right diameter must be at least 0")
        .default(0)
        .typeError("Right diameter must be a number"),
    })
    .required("Right lense details are required"),
  left: Yup.object()
    .shape({
      selectedLense: Yup.string()
        .trim()
        .matches(objectIdPattern, "Left lense selection is required")
        .required("Left lense selection is required"),
      diameter: Yup.number()
        .min(0, "Left diameter must be at least 0")
        .default(0)
        .typeError("Left diameter must be a number"),
    })
    .required("Left lense details are required"),
});

// BASE ORDER FORM VALIDATION SCHEMA
const orderFormValidationSchema = Yup.object().shape({
  customer: Yup.string()
    .trim()
    .matches(objectIdPattern, "Select a valid customer")
    .required("Select a valid customer"),
  eyeCheckingNumber: Yup.string()
    .trim()
    .required("Eye checking number is required"),
  CR39: Yup.boolean().default(false),
  PHOTO_GREY: Yup.boolean().default(false),
  MULTI_COATED: Yup.boolean().default(false),
  PROGRESSIVE: Yup.boolean().default(false),
  EASY_FIT: Yup.boolean().default(false),
  hb: Yup.string().trim().nullable(),
  distance: Yup.object()
    .shape({
      right: eyeMeasurementSchema.required(
        "Distance right eye measurement is required",
      ),
      left: eyeMeasurementSchema.required(
        "Distance left eye measurement is required",
      ),
    })
    .required("Distance measurements are required"),
  near: Yup.object()
    .shape({
      right: eyeMeasurementSchema.required(
        "Near right eye measurement is required",
      ),
      left: eyeMeasurementSchema.required(
        "Near left eye measurement is required",
      ),
    })
    .required("Near measurements are required"),
  frame: Yup.mixed().when("orderSchedule", {
    is: enums.PLACE_ORDER.NOW,
    then: () => frameSchema,
    otherwise: () => Yup.mixed().nullable(),
  }),
  lense: Yup.mixed().when("orderSchedule", {
    is: enums.PLACE_ORDER.NOW,
    then: () => lenseSchema,
    otherwise: () => Yup.mixed().nullable(),
  }),
  discount: Yup.number()
    .min(0, "Discount must be a positive number or zero")
    .default(0)
    .typeError("Discount must be a number"),
  testDate: Yup.date()
    .required("Test date is required")
    .typeError("Test date must be a valid date"),
});

export { orderFormValidationSchema };
